import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, FileText, Clock, CheckCircle, XCircle, AlertTriangle, Eye, Send, Edit, Loader, Download, Filter, User, Building, Calendar, TrendingUp, Shield, ExternalLink } from 'lucide-react';
import api from '../services/api';
import RecurribilityBadge from '../components/RecurribilityBadge';
import FuenteVerificada, { ListaFuentesVerificadas, VerificacionCitasBadge } from '../components/FuenteVerificada';

function AdminRecursos() {
  const navigate = useNavigate();
  const [recursos, setRecursos] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [vista, setVista] = useState('lista'); // lista, revision
  const [recursoActivo, setRecursoActivo] = useState(null);
  const [textoEditado, setTextoEditado] = useState('');
  const [notas, setNotas] = useState('');
  const [procesando, setProcesando] = useState(false);

  // Para marcar como enviado
  const [datosEnvio, setDatosEnvio] = useState({ numero_registro: '', organismo: '', via_envio: 'electronica' });

  // Para registrar resolución
  const [datosResolucion, setDatosResolucion] = useState({ resolucion: '', motivo: '', importe_devuelto: 0, puntos_devueltos: 0 });

  // Para aprobacion con numero de colegiado
  const [numeroColegiado, setNumeroColegiado] = useState('');

  // Citas y fuentes del recurso
  const [citasRecurso, setCitasRecurso] = useState([]);
  const [cargandoCitas, setCargandoCitas] = useState(false);
  const [cargandoRevision, setCargandoRevision] = useState(false);
  const [errorRevision, setErrorRevision] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, [filtroEstado]);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      const [recursosRes, estadisticasRes] = await Promise.all([
        api.get(`/recursos/admin/todos${filtroEstado ? `?estado=${filtroEstado}` : ''}`),
        api.get('/recursos/admin/estadisticas')
      ]);

      setRecursos(recursosRes.data.recursos || []);
      setEstadisticas(estadisticasRes.data.estadisticas);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirRevision = async (recurso) => {
    try {
      setCargandoRevision(true);
      setErrorRevision(null);
      const response = await api.get(`/recursos/${recurso.id}`);
      setRecursoActivo(response.data.recurso);
      setTextoEditado(response.data.recurso.texto_final || response.data.recurso.borrador_texto || '');
      setNotas('');
      setNumeroColegiado(response.data.recurso.numero_colegiado_abogado || '');
      setVista('revision');

      // Cargar citas del recurso
      cargarCitasRecurso(recurso.id);
    } catch (error) {
      console.error('Error cargando recurso:', error);
      const mensaje = error.response?.data?.error || 'Error al cargar el recurso. Intente de nuevo.';
      setErrorRevision(mensaje);
      alert(mensaje);
    } finally {
      setCargandoRevision(false);
    }
  };

  const cargarCitasRecurso = async (recursoId) => {
    try {
      setCargandoCitas(true);
      const response = await api.get(`/fuentes/recurso/${recursoId}`);
      setCitasRecurso(response.data.citas || []);
    } catch (error) {
      console.error('Error cargando citas:', error);
      setCitasRecurso([]);
    } finally {
      setCargandoCitas(false);
    }
  };

  const asignarmeRecurso = async (recursoId) => {
    try {
      await api.post(`/recursos/admin/${recursoId}/asignar`, {});
      alert('Recurso asignado. Ahora puedes revisarlo.');
      cargarDatos();
    } catch (error) {
      console.error('Error asignando recurso:', error);
      alert(error.response?.data?.error || 'Error al asignar el recurso');
    }
  };

  const aprobarRecurso = async () => {
    if (!recursoActivo) return;

    // Validar numero de colegiado
    if (!numeroColegiado.trim()) {
      alert('Debe ingresar su numero de colegiado para aprobar el recurso');
      return;
    }

    try {
      setProcesando(true);
      await api.post(`/recursos/admin/${recursoActivo.id}/aprobar`, {
        texto_final: textoEditado,
        notas,
        numero_colegiado: numeroColegiado.trim()
      });

      alert('Recurso aprobado correctamente');
      setVista('lista');
      setNumeroColegiado('');
      setCitasRecurso([]);
      cargarDatos();
    } catch (error) {
      console.error('Error aprobando recurso:', error);
      alert(error.response?.data?.error || 'Error al aprobar el recurso');
    } finally {
      setProcesando(false);
    }
  };

  const rechazarRecurso = async () => {
    if (!recursoActivo || !notas) {
      alert('Debe indicar el motivo del rechazo');
      return;
    }
    try {
      setProcesando(true);
      await api.post(`/recursos/admin/${recursoActivo.id}/rechazar`, { motivo: notas });

      alert('Recurso rechazado');
      setVista('lista');
      cargarDatos();
    } catch (error) {
      console.error('Error rechazando recurso:', error);
    } finally {
      setProcesando(false);
    }
  };

  const marcarEnviado = async () => {
    if (!recursoActivo || !datosEnvio.numero_registro) {
      alert('Debe indicar el número de registro');
      return;
    }
    try {
      setProcesando(true);
      await api.post(`/recursos/admin/${recursoActivo.id}/enviar`, datosEnvio);

      alert('Recurso marcado como enviado');
      setVista('lista');
      cargarDatos();
    } catch (error) {
      console.error('Error marcando envío:', error);
    } finally {
      setProcesando(false);
    }
  };

  const registrarResolucion = async () => {
    if (!recursoActivo || !datosResolucion.resolucion) {
      alert('Debe seleccionar la resolución');
      return;
    }
    try {
      setProcesando(true);
      await api.post(`/recursos/admin/${recursoActivo.id}/resolucion`, datosResolucion);

      alert('Resolución registrada');
      setVista('lista');
      cargarDatos();
    } catch (error) {
      console.error('Error registrando resolución:', error);
    } finally {
      setProcesando(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      solicitado: { color: 'bg-blue-100 text-blue-800', label: 'Pendiente' },
      en_revision: { color: 'bg-yellow-100 text-yellow-800', label: 'En Revisión' },
      aprobado: { color: 'bg-green-100 text-green-800', label: 'Aprobado' },
      rechazado: { color: 'bg-red-100 text-red-800', label: 'Rechazado' },
      enviado: { color: 'bg-purple-100 text-purple-800', label: 'Enviado' },
      resuelto: { color: 'bg-gray-100 text-gray-800', label: 'Resuelto' }
    };
    const config = estados[estado] || estados.solicitado;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getProbabilidadColor = (prob) => {
    if (prob >= 70) return 'text-green-600';
    if (prob >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Vista de revisión
  if (vista === 'revision' && recursoActivo) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => setVista('lista')} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4" /> Volver a la lista
          </button>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Panel izquierdo - Datos */}
            <div className="lg:col-span-1 space-y-4">
              {/* Estado actual */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Estado del Recurso</h3>
                <div className="flex items-center gap-2 mb-3">
                  {getEstadoBadge(recursoActivo.estado)}
                  <span className="text-sm text-gray-500">ID: #{recursoActivo.id}</span>
                </div>
                <div className={`text-2xl font-bold ${getProbabilidadColor(recursoActivo.probabilidad_exito)}`}>
                  {recursoActivo.probabilidad_exito}% probabilidad
                </div>
              </div>

              {/* Datos de la multa */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Datos de la Multa</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Matrícula:</span><span className="font-medium">{recursoActivo.matricula}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Fecha:</span><span>{new Date(recursoActivo.fecha_infraccion).toLocaleDateString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Importe:</span><span className="font-medium">{recursoActivo.importe}€</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Puntos:</span><span>{recursoActivo.puntos || 0}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Organismo:</span><span>{recursoActivo.organismo || 'DGT'}</span></div>
                </div>
                {recursoActivo.descripcion && (
                  <p className="mt-3 text-sm text-gray-600 border-t pt-3">{recursoActivo.descripcion}</p>
                )}
              </div>

              {/* Datos empresa */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Empresa</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Building className="w-4 h-4 text-gray-400" /><span>{recursoActivo.empresa_nombre}</span></div>
                  <div className="text-gray-500">CIF: {recursoActivo.empresa_cif}</div>
                </div>
              </div>

              {/* Análisis IA */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Analisis IA</h3>
                {recursoActivo.factores_positivos && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 uppercase mb-1">Factores a favor</p>
                    {(Array.isArray(recursoActivo.factores_positivos)
                      ? recursoActivo.factores_positivos
                      : JSON.parse(recursoActivo.factores_positivos)
                    ).map((f, i) => (
                      <div key={i} className="text-sm text-green-700 bg-green-50 rounded p-2 mb-1">
                        {typeof f === 'string' ? f : f.factor}
                      </div>
                    ))}
                  </div>
                )}
                {recursoActivo.factores_negativos && (Array.isArray(recursoActivo.factores_negativos)
                  ? recursoActivo.factores_negativos
                  : JSON.parse(recursoActivo.factores_negativos)
                ).length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Factores en contra</p>
                    {(Array.isArray(recursoActivo.factores_negativos)
                      ? recursoActivo.factores_negativos
                      : JSON.parse(recursoActivo.factores_negativos)
                    ).map((f, i) => (
                      <div key={i} className="text-sm text-red-700 bg-red-50 rounded p-2 mb-1">
                        {typeof f === 'string' ? f : f.factor}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Fuentes y Citas Legales */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Fuentes Legales
                  </h3>
                  {citasRecurso.length > 0 && (
                    <VerificacionCitasBadge
                      verificadas={citasRecurso.filter(c => c.fue_verificada).length}
                      total={citasRecurso.length}
                    />
                  )}
                </div>

                {cargandoCitas ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : citasRecurso.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {citasRecurso.map((cita, i) => (
                      <FuenteVerificada
                        key={cita.id || i}
                        fuente={cita}
                        compact={true}
                        verificada={cita.fue_verificada}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hay citas legales registradas
                  </p>
                )}

                {citasRecurso.filter(c => !c.fue_verificada).length > 0 && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    {citasRecurso.filter(c => !c.fue_verificada).length} citas pendientes de verificacion
                  </div>
                )}
              </div>

              {/* Disclaimer aceptado */}
              {recursoActivo.disclaimer_aceptado && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Aviso legal aceptado por el cliente</span>
                  </div>
                </div>
              )}
            </div>

            {/* Panel derecho - Edición */}
            <div className="lg:col-span-2 space-y-4">
              {/* Editor de texto */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Texto del Recurso</h3>
                <textarea
                  value={textoEditado}
                  onChange={(e) => setTextoEditado(e.target.value)}
                  rows={20}
                  className="w-full border border-gray-200 rounded-lg p-4 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Texto del recurso..."
                />
              </div>

              {/* Notas del abogado */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-medium text-gray-700 mb-3">Notas / Observaciones</h3>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Notas internas, motivo de rechazo..."
                />
              </div>

              {/* Acciones según estado */}
              {recursoActivo.estado === 'solicitado' || recursoActivo.estado === 'en_revision' ? (
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="font-medium text-gray-700 mb-3">Acciones de Abogado</h3>

                  {/* Numero de colegiado */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-1">
                      Numero de Colegiado <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={numeroColegiado}
                      onChange={(e) => setNumeroColegiado(e.target.value)}
                      placeholder="Ej: 12345/ICAM"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Requerido para aprobar el recurso
                    </p>
                  </div>

                  {/* Advertencia si hay citas sin verificar */}
                  {citasRecurso.filter(c => !c.fue_verificada).length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            Hay citas pendientes de verificacion
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Revise el texto y asegurese de que las fuentes son correctas antes de aprobar.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button onClick={aprobarRecurso} disabled={procesando || !numeroColegiado.trim()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium">
                      <CheckCircle className="w-5 h-5" /> Aprobar
                    </button>
                    <button onClick={rechazarRecurso} disabled={procesando}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">
                      <XCircle className="w-5 h-5" /> Rechazar
                    </button>
                  </div>
                </div>
              ) : null}

              {recursoActivo.estado === 'aprobado' && (
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="font-medium text-gray-700 mb-3">Marcar como Enviado</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <input type="text" placeholder="Nº de Registro" value={datosEnvio.numero_registro}
                      onChange={(e) => setDatosEnvio({ ...datosEnvio, numero_registro: e.target.value })}
                      className="border rounded-lg px-3 py-2" />
                    <input type="text" placeholder="Organismo destino" value={datosEnvio.organismo}
                      onChange={(e) => setDatosEnvio({ ...datosEnvio, organismo: e.target.value })}
                      className="border rounded-lg px-3 py-2" />
                  </div>
                  <select value={datosEnvio.via_envio} onChange={(e) => setDatosEnvio({ ...datosEnvio, via_envio: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 mb-4">
                    <option value="electronica">Sede electrónica</option>
                    <option value="correo">Correo certificado</option>
                    <option value="presencial">Presencial</option>
                  </select>
                  <button onClick={marcarEnviado} disabled={procesando}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
                    <Send className="w-5 h-5" /> Marcar como Enviado
                  </button>
                </div>
              )}

              {recursoActivo.estado === 'enviado' && (
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="font-medium text-gray-700 mb-3">Registrar Resolución</h3>
                  <select value={datosResolucion.resolucion} onChange={(e) => setDatosResolucion({ ...datosResolucion, resolucion: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 mb-4">
                    <option value="">Seleccionar resolución...</option>
                    <option value="estimado">✅ ESTIMADO (a favor)</option>
                    <option value="desestimado">❌ DESESTIMADO (en contra)</option>
                    <option value="parcial">⚠️ ESTIMADO PARCIALMENTE</option>
                  </select>
                  <textarea value={datosResolucion.motivo} onChange={(e) => setDatosResolucion({ ...datosResolucion, motivo: e.target.value })}
                    placeholder="Motivo de la resolución..." rows={2} className="w-full border rounded-lg px-3 py-2 mb-4" />
                  {(datosResolucion.resolucion === 'estimado' || datosResolucion.resolucion === 'parcial') && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-sm text-gray-600">Importe devuelto €</label>
                        <input type="number" value={datosResolucion.importe_devuelto}
                          onChange={(e) => setDatosResolucion({ ...datosResolucion, importe_devuelto: parseFloat(e.target.value) || 0 })}
                          className="w-full border rounded-lg px-3 py-2" />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Puntos devueltos</label>
                        <input type="number" value={datosResolucion.puntos_devueltos}
                          onChange={(e) => setDatosResolucion({ ...datosResolucion, puntos_devueltos: parseInt(e.target.value) || 0 })}
                          className="w-full border rounded-lg px-3 py-2" />
                      </div>
                    </div>
                  )}
                  <button onClick={registrarResolucion} disabled={procesando}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                    <CheckCircle className="w-5 h-5" /> Registrar Resolución
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista principal - Lista
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-2">
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Recursos</h1>
            <p className="text-gray-600">Panel de revisión y seguimiento de recursos</p>
          </div>
        </div>

        {errorRevision && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
            <span>{errorRevision}</span>
            <button onClick={() => setErrorRevision(null)} className="text-red-500 hover:text-red-700">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Estadísticas */}
            {estadisticas && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-2xl font-bold text-gray-900">{estadisticas.total || 0}</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
                <div className="bg-blue-50 rounded-xl shadow-sm p-4 border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{estadisticas.solicitados || 0}</div>
                  <div className="text-sm text-blue-600">Pendientes</div>
                </div>
                <div className="bg-yellow-50 rounded-xl shadow-sm p-4 border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">{estadisticas.en_revision || 0}</div>
                  <div className="text-sm text-yellow-600">En Revisión</div>
                </div>
                <div className="bg-purple-50 rounded-xl shadow-sm p-4 border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{estadisticas.enviados || 0}</div>
                  <div className="text-sm text-purple-600">Enviados</div>
                </div>
                <div className="bg-green-50 rounded-xl shadow-sm p-4 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{estadisticas.tasa_exito_real || 0}%</div>
                  <div className="text-sm text-green-600">Tasa Éxito</div>
                </div>
                <div className="bg-green-50 rounded-xl shadow-sm p-4 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{estadisticas.total_devuelto || 0}€</div>
                  <div className="text-sm text-green-600">Recuperado</div>
                </div>
              </div>
            )}

            {/* Filtros */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div className="flex items-center gap-4">
                <Filter className="w-5 h-5 text-gray-400" />
                <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
                  className="border rounded-lg px-3 py-2">
                  <option value="">Todos los estados</option>
                  <option value="solicitado">Pendientes de revisión</option>
                  <option value="en_revision">En revisión</option>
                  <option value="aprobado">Aprobados (sin enviar)</option>
                  <option value="enviado">Enviados</option>
                  <option value="resuelto">Resueltos</option>
                  <option value="rechazado">Rechazados</option>
                </select>
              </div>
            </div>

            {/* Lista de recursos */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {recursos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay recursos {filtroEstado ? 'con este estado' : ''}</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Empresa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Matrícula</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fecha Multa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Importe</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Prob.</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Resolución</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recursos.map(recurso => (
                      <tr key={recurso.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">#{recurso.id}</td>
                        <td className="py-3 px-4">
                          <div className="text-sm">{recurso.empresa_nombre}</div>
                          <div className="text-xs text-gray-500">{recurso.empresa_cif}</div>
                        </td>
                        <td className="py-3 px-4 font-medium">{recurso.matricula}</td>
                        <td className="py-3 px-4 text-sm">{new Date(recurso.fecha_infraccion).toLocaleDateString()}</td>
                        <td className="py-3 px-4 font-medium">{recurso.importe_multa}€</td>
                        <td className="py-3 px-4">
                          <span className={`font-bold ${getProbabilidadColor(recurso.probabilidad_exito)}`}>
                            {recurso.probabilidad_exito}%
                          </span>
                        </td>
                        <td className="py-3 px-4">{getEstadoBadge(recurso.estado)}</td>
                        <td className="py-3 px-4">
                          {recurso.resolucion ? (
                            <span className={`text-sm font-medium ${recurso.resolucion === 'estimado' ? 'text-green-600' : recurso.resolucion === 'parcial' ? 'text-yellow-600' : 'text-red-600'}`}>
                              {recurso.resolucion === 'estimado' ? '✅' : recurso.resolucion === 'parcial' ? '⚠️' : '❌'} {recurso.resolucion}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {recurso.estado === 'solicitado' && (
                              <button
                                onClick={() => asignarmeRecurso(recurso.id)}
                                title="Asignar este recurso a mi usuario para revisarlo"
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1">
                                <User className="w-3 h-3" /> Asignarme
                              </button>
                            )}
                            <button
                              onClick={() => abrirRevision(recurso)}
                              disabled={cargandoRevision}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1 disabled:opacity-50">
                              {cargandoRevision ? <Loader className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                              {cargandoRevision ? 'Cargando...' : 'Ver detalle'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminRecursos;
